import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * A minimal, unobtrusive top progress bar that reacts to route changes.
 * - Delays appearance to avoid flicker on fast navigations
 * - Advances to ~85% while the new route paints, then completes
 * - Respects prefers-reduced-motion by disabling sweeping animations
 */
export function RouteProgressBar() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);
  const finishRef = useRef<number | null>(null);

  // Motion preference
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Clean up any pending timers
  const clearTimers = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (finishRef.current) {
      window.clearTimeout(finishRef.current);
      finishRef.current = null;
    }
  };

  // Start the bar on location change
  useEffect(() => {
    clearTimers();
    setProgress(0);

    // Delay to prevent flashes on instant navigations
    const startTimeout = window.setTimeout(() => {
      setVisible(true);

      // Smoothly increment progress up to ~85%
      timerRef.current = window.setInterval(() => {
        setProgress((p) => {
          if (p < 85) {
            const step = prefersReducedMotion ? 20 : Math.max(1, 6 - Math.min(5, Math.floor(p / 20)));
            return Math.min(85, p + step);
          }
          return p;
        });
      }, prefersReducedMotion ? 120 : 90);
    }, 120);

    // Finish shortly after mount to keep bar brief
    finishRef.current = window.setTimeout(() => {
      setProgress(100);
      // Give CSS time to paint 100% then hide
      window.setTimeout(() => setVisible(false), prefersReducedMotion ? 120 : 200);
    }, 700);

    return () => {
      window.clearTimeout(startTimeout);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-transparent"
      aria-hidden="true"
    >
      <div
        className="h-full bg-gradient-to-r from-primary/70 via-primary to-primary/70 shadow-[0_0_8px_rgba(0,0,0,0.1)]"
        style={{ width: `${Math.max(0, Math.min(100, progress))}%`, transition: prefersReducedMotion ? "none" : "width 120ms ease" }}
      />
    </div>
  );
}

export default RouteProgressBar;


